import { useState, ReactNode } from "react";
import { NotificationChannel } from "../../types/alerts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface CreateChannelDialogProps {
  children: ReactNode;
  onChannelCreated: (channel: NotificationChannel) => void;
}

export function CreateChannelDialog({ children, onChannelCreated }: CreateChannelDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<"email" | "slack" | "webhook" | "pagerduty" | "jira">("email");
  const [config, setConfig] = useState("");
  const [jiraProjectKey, setJiraProjectKey] = useState("");
  const [jiraAssignee, setJiraAssignee] = useState("");
  const [jiraSlaMinutes, setJiraSlaMinutes] = useState<number>(60);

  const handleSubmit = () => {
    if (!name.trim()) return;
    if (type !== "jira" && !config.trim()) return;
    if (type === "jira" && !jiraProjectKey.trim()) return;

    const channelConfig: Record<string, string | number> = {};
    if (type === "email") {
      channelConfig.email = config.trim();
    } else if (type === "slack") {
      channelConfig.slackChannel = config.trim();
    } else if (type === "webhook") {
      channelConfig.webhookUrl = config.trim();
    } else if (type === "pagerduty") {
      channelConfig.serviceKey = config.trim();
    } else if (type === "jira") {
      channelConfig.jiraProjectKey = jiraProjectKey.trim();
      channelConfig.jiraIssueType = "Bug"; // Default, can be customized
      if (jiraAssignee.trim()) {
        channelConfig.jiraAssignee = jiraAssignee.trim();
      }
      if (jiraSlaMinutes > 0) {
        channelConfig.jiraSlaMinutes = jiraSlaMinutes;
      }
    }

    const newChannel: NotificationChannel = {
      id: `channel-${Date.now()}`,
      name: name.trim(),
      type,
      enabled: true,
      config: channelConfig,
    };

    onChannelCreated(newChannel);
    setName("");
    setConfig("");
    setJiraProjectKey("");
    setJiraAssignee("");
    setJiraSlaMinutes(60);
    setIsOpen(false);
  };

  const getConfigLabel = () => {
    switch (type) {
      case "email":
        return "Email Address";
      case "slack":
        return "Slack Channel (e.g., #channel-name)";
      case "webhook":
        return "Webhook URL";
      case "pagerduty":
        return "Service Key";
      case "jira":
        return "Jira Instance URL";
    }
  };

  const getConfigPlaceholder = () => {
    switch (type) {
      case "email":
        return "team@example.com";
      case "slack":
        return "#alerts";
      case "webhook":
        return "https://hooks.example.com/webhook";
      case "pagerduty":
        return "pagerduty-service-key";
      case "jira":
        return "https://yourcompany.atlassian.net";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Notification Channel</DialogTitle>
          <DialogDescription>Add a new notification channel to receive alerts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="channel-name">Channel Name</Label>
            <Input
              id="channel-name"
              placeholder="e.g., Team Email, Slack Channel"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="channel-type">Channel Type</Label>
            <Select
              value={type}
              onValueChange={(value) =>
                setType(value as "email" | "slack" | "webhook" | "pagerduty" | "jira")
              }
            >
              <SelectTrigger id="channel-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="slack">Slack</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
                <SelectItem value="pagerduty">PagerDuty</SelectItem>
                <SelectItem value="jira">Jira</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === "jira" ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="jira-project-key">Jira Project Key *</Label>
                <Input
                  id="jira-project-key"
                  placeholder="e.g., SEC"
                  value={jiraProjectKey}
                  onChange={(e) => setJiraProjectKey(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jira-assignee">Assignee (username or email)</Label>
                <Input
                  id="jira-assignee"
                  placeholder="e.g., jdoe or jdoe@example.com"
                  value={jiraAssignee}
                  onChange={(e) => setJiraAssignee(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jira-sla">SLA (minutes)</Label>
                <Input
                  id="jira-sla"
                  type="number"
                  min={1}
                  placeholder="60"
                  value={jiraSlaMinutes}
                  onChange={(e) => setJiraSlaMinutes(Number(e.target.value) || 60)}
                />
                <p className="text-xs text-muted-foreground">
                  SLA in minutes for ticket response time
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="channel-config">{getConfigLabel()}</Label>
              <Input
                id="channel-config"
                placeholder={getConfigPlaceholder()}
                value={config}
                onChange={(e) => setConfig(e.target.value)}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !name.trim() ||
              (type === "jira" ? !jiraProjectKey.trim() : !config.trim())
            }
          >
            Create Channel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
